import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  Upload,
  CheckCircle,
  Clock,
  FileText,
  Lock,
  Eye,
  EyeOff,
  Send,
  Search,
  Copy,
  Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Report categories
const CATEGORIES = [
  { value: "Assédio Moral", label: "Assédio Moral", icon: "😔" },
  { value: "Assédio Sexual", label: "Assédio Sexual", icon: "⚠️" },
  { value: "Fraude", label: "Fraude", icon: "💰" },
  { value: "Corrupção", label: "Corrupção", icon: "🔒" },
  { value: "Discriminação", label: "Discriminação", icon: "🚫" },
  { value: "Desvio de Conduta", label: "Desvio de Conduta", icon: "📋" },
  { value: "Violação de Segurança", label: "Violação de Segurança", icon: "🛡️" },
  { value: "Condições de Trabalho", label: "Condições de Trabalho", icon: "🏭" },
  { value: "Outros", label: "Outros", icon: "📝" }
];

const WhistleblowerPage = () => {
  const { companySlug } = useParams();
  const [mode, setMode] = useState('submit'); // 'submit' or 'check'
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [trackingId, setTrackingId] = useState('');
  
  const [companyNotFound, setCompanyNotFound] = useState(false);
  
  // Form state
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Check status state
  const [checkProtocol, setCheckProtocol] = useState('');
  const [reportStatus, setReportStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    fetchCompanyInfo();
  }, [companySlug]);

  const fetchCompanyInfo = async () => {
    try {
      const response = await fetch(`${API}/api/whistleblower/company/${companySlug}`);
      if (response.ok) {
        const data = await response.json();
        setCompanyName(data.company_name);
        setCompanyNotFound(false);
      } else {
        setCompanyNotFound(true);
        toast.error('Empresa não encontrada');
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setCompanyNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        // Create form data for upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'whistleblower');

        const response = await fetch(`${API}/api/storage/upload`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.url);
        } else {
          toast.error(`Erro ao enviar arquivo: ${file.name}`);
        }
      }

      setAttachments([...attachments, ...uploadedUrls]);
      if (uploadedUrls.length > 0) {
        toast.success(`${uploadedUrls.length} arquivo(s) enviado(s)`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao enviar arquivos');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!category) {
      toast.error('Selecione uma categoria');
      return;
    }
    
    if (description.length < 10) {
      toast.error('A descrição deve ter pelo menos 10 caracteres');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API}/api/whistleblower/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_slug: companySlug,
          category,
          description,
          attachments
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTrackingId(data.tracking_id);
        setSubmitted(true);
        toast.success('Denúncia enviada com sucesso!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao enviar denúncia');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Erro ao enviar denúncia. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    
    if (!checkProtocol.trim()) {
      toast.error('Digite o protocolo da denúncia');
      return;
    }

    setCheckingStatus(true);
    setReportStatus(null);

    try {
      const response = await fetch(`${API}/api/whistleblower/report/${checkProtocol.trim()}`);
      
      if (response.ok) {
        const data = await response.json();
        setReportStatus(data);
      } else if (response.status === 404) {
        toast.error('Protocolo não encontrado');
      } else {
        toast.error('Erro ao consultar status');
      }
    } catch (error) {
      console.error('Check status error:', error);
      toast.error('Erro ao consultar status');
    } finally {
      setCheckingStatus(false);
    }
  };

  const copyProtocol = () => {
    navigator.clipboard.writeText(trackingId);
    toast.success('Protocolo copiado!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-300';
      case 'dismissed': return 'bg-slate-100 text-slate-800 border-slate-300';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Show error page if company not found
  if (companyNotFound) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Empresa não encontrada</h2>
            <p className="text-slate-400 mb-6">
              O link acessado não corresponde a nenhuma empresa cadastrada no sistema. 
              Verifique se o link está correto ou entre em contato com a administração.
            </p>
            <div className="text-sm text-slate-500 bg-slate-900/50 rounded-lg p-3">
              <p className="font-mono break-all">{companySlug}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-700 py-6">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-teal-400" />
            <h1 className="text-2xl font-bold text-white">Canal de Denúncias</h1>
          </div>
          <p className="text-slate-400">{companyName}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Anonymity Banner */}
        <Card className="mb-6 bg-teal-900/30 border-teal-600/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Lock className="h-6 w-6 text-teal-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-teal-300 mb-1">Sua identidade está protegida</h3>
                <p className="text-sm text-slate-300">
                  Este canal garante <strong>anonimato total</strong>. Não registramos IP, cookies, nem qualquer 
                  informação que possa identificá-lo. Você receberá apenas um protocolo para acompanhar sua denúncia.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={mode === 'submit' ? 'default' : 'outline'}
            onClick={() => setMode('submit')}
            className={mode === 'submit' ? 'bg-teal-600 hover:bg-teal-700' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
          >
            <Send className="h-4 w-4 mr-2" />
            Nova Denúncia
          </Button>
          <Button
            variant={mode === 'check' ? 'default' : 'outline'}
            onClick={() => setMode('check')}
            className={mode === 'check' ? 'bg-teal-600 hover:bg-teal-700' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
          >
            <Search className="h-4 w-4 mr-2" />
            Consultar Status
          </Button>
        </div>

        {mode === 'submit' && !submitted && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                Registrar Denúncia Anônima
              </CardTitle>
              <CardDescription className="text-slate-400">
                Descreva a situação com o máximo de detalhes possível. Todas as informações são confidenciais.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Selection */}
                <div className="space-y-2">
                  <Label className="text-slate-200">Categoria da Denúncia *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setCategory(cat.value)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          category === cat.value
                            ? 'bg-teal-600 border-teal-500 text-white'
                            : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        <span className="text-lg mr-2">{cat.icon}</span>
                        <span className="text-sm">{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-slate-200">Descrição Detalhada *</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o ocorrido com o máximo de detalhes: o que aconteceu, quando, onde, quem estava envolvido (se souber), e qualquer outra informação relevante..."
                    className="min-h-[200px] bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    required
                  />
                  <p className="text-xs text-slate-500">Mínimo de 10 caracteres. Quanto mais detalhes, melhor a investigação.</p>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label className="text-slate-200">Anexos (opcional)</Label>
                  <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className={`h-8 w-8 ${uploading ? 'text-teal-400 animate-pulse' : 'text-slate-500'}`} />
                      <span className="text-sm text-slate-400">
                        {uploading ? 'Enviando...' : 'Clique para anexar arquivos'}
                      </span>
                      <span className="text-xs text-slate-500">
                        Imagens, PDFs, documentos (máx. 10MB cada)
                      </span>
                    </label>
                  </div>
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {attachments.map((url, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-slate-700 text-slate-300">
                          <FileText className="h-3 w-3 mr-1" />
                          Arquivo {idx + 1}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={submitting || !category || description.length < 10}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Enviar Denúncia Anônima
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Success Screen */}
        {mode === 'submit' && submitted && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Denúncia Registrada!</h2>
              <p className="text-slate-400 mb-6">
                Sua denúncia foi recebida e será analisada pela equipe responsável.
              </p>
              
              <div className="bg-slate-900 rounded-lg p-6 mb-6">
                <p className="text-sm text-slate-400 mb-2">Seu protocolo de acompanhamento:</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl font-mono font-bold text-teal-400">{trackingId}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyProtocol}
                    className="text-slate-400 hover:text-white"
                  >
                    <Copy className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-200 text-left">
                    <strong>Importante:</strong> Guarde este protocolo! Ele é a única forma de acompanhar 
                    o status da sua denúncia. Não será possível recuperá-lo posteriormente.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => {
                    setCheckProtocol(trackingId);
                    setMode('check');
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Consultar Status
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSubmitted(false);
                    setCategory('');
                    setDescription('');
                    setAttachments([]);
                    setTrackingId('');
                  }}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Nova Denúncia
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check Status Mode */}
        {mode === 'check' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Search className="h-5 w-5 text-teal-400" />
                Consultar Denúncia
              </CardTitle>
              <CardDescription className="text-slate-400">
                Digite o protocolo recebido para verificar o status da sua denúncia.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCheckStatus} className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={checkProtocol}
                    onChange={(e) => setCheckProtocol(e.target.value.toUpperCase())}
                    placeholder="CERP-XXXX"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 font-mono text-lg"
                  />
                  <Button
                    type="submit"
                    disabled={checkingStatus}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {checkingStatus ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Search className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </form>

              {reportStatus && (
                <div className="mt-6 space-y-4">
                  <div className="bg-slate-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-slate-400">Protocolo</span>
                      <span className="font-mono text-teal-400">{reportStatus.tracking_id}</span>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-slate-400">Categoria</span>
                      <span className="text-white">{reportStatus.category}</span>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-slate-400">Status</span>
                      <Badge className={getStatusColor(reportStatus.status)}>
                        {reportStatus.status_display}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">Registrado em</span>
                      <span className="text-white">
                        {new Date(reportStatus.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>

                  {reportStatus.admin_response && (
                    <div className="bg-teal-900/30 border border-teal-600/50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-teal-300 mb-2 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Resposta da Empresa
                      </h4>
                      <p className="text-slate-300 whitespace-pre-wrap">{reportStatus.admin_response}</p>
                    </div>
                  )}

                  {!reportStatus.admin_response && reportStatus.status === 'pending' && (
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <Clock className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">
                        Sua denúncia está aguardando análise. Volte em breve para verificar atualizações.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Canal de Denúncias powered by CERP Brasil</p>
          <p className="mt-1">Anonimato garantido • Sem rastreamento • Confidencial</p>
        </div>
      </div>
    </div>
  );
};

export default WhistleblowerPage;
